import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { locationHubService } from '../../services/locationHubService';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import { Plus, Filter, MapPin, ArrowLeft, Pencil } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { destinationTypeLabel, formatHubBranchLabel } from '../../utils/destinationType.js';
import { downloadHubAttendanceQr } from './downloadHubAttendanceQr.js';
import { canScanQrAttendance } from '../../utils/networkMode.js';

const ViewLocationHub = () => {
  const { t } = useTranslation();
  const { hubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hubData, setHubData] = useState(null);
  const [companyHubs, setCompanyHubs] = useState([]);

  useEffect(() => {
    fetchHub();
  }, [hubId]);

  const fetchHub = async () => {
    try {
      setLoading(true);
      const response = await locationHubService.getHub(hubId);
      const hub = response.data.hub || response.data.data;
      
      if (hub) {
        setHubData(hub);
        try {
          const filters = hub.internal_company_id ? { company_id: hub.internal_company_id } : {};
          const hubsRes = await locationHubService.getHubsForSelect(filters);
          setCompanyHubs(hubsRes.data.hubs || hubsRes.data.data || []);
        } catch {
          setCompanyHubs([]);
        }
      } else {
        Alert.error('Destination hub not found');
      }
    } catch (error) {
      console.error('Error fetching hub:', error);
      Alert.error(error.response?.data?.error || 'Failed to load destination hub');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading destination hub..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!hubData) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/location-hubs')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Location Hubs
          </button>
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <p className="text-gray-600">Destination hub not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/location-hubs')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Location Hubs
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-8 h-8 text-buttonBg flex-shrink-0" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-secondary">{hubData.location}</h1>
                <p className="text-gray-600 text-sm sm:text-base">{hubData.hub_id}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-blue-100 text-blue-800 text-center">
                {(hubData.destination_type_label || destinationTypeLabel(hubData.destination_type)).toUpperCase()}
              </span>
              {canScanQrAttendance(user) && (
                <Button
                  variant="secondary"
                  onClick={() => downloadHubAttendanceQr(hubData)}
                >
                  Download Attendance QR
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Details */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-secondary" />
            Hub Information
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
            {/* Destination */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">{t('pages.locationHub.hubBranchColumn', { defaultValue: 'Hub/Branch' })}</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.location}</p>
            </div>

            {/* Destination Code */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Location Code</p>
              <p className="font-semibold text-secondary text-base sm:text-lg font-mono">{hubData.location_code}</p>
            </div>

            {/* Commission % */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">{t('reports.avgCommissionPct')}</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {hubData.commission_percentage !== null && hubData.commission_percentage !== undefined ? `${hubData.commission_percentage}%` : 'N/A'}
              </p>
            </div>

            {/* Rate / KG */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Rate / KG</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {hubData.rate_per_kg !== null && hubData.rate_per_kg !== undefined && hubData.rate_per_kg !== ''
                  ? `₹${hubData.rate_per_kg}`
                  : 'N/A'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Monthly revenue target</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {hubData.monthly_revenue_target != null && hubData.monthly_revenue_target !== ''
                  ? `₹${hubData.monthly_revenue_target}`
                  : 'Not set'}
              </p>
            </div>

            {hubData.ownership_type && (
              <div className="bg-gray-50 rounded-xl p-4 border border-primary">
                <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Ownership Type</p>
                <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.ownership_type}</p>
              </div>
            )}

            {hubData.dispatch_types && (
              <div className="bg-gray-50 rounded-xl p-4 border border-primary">
                <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Dispatch Types</p>
                <p className="font-semibold text-secondary text-base sm:text-lg">
                  {(Array.isArray(hubData.dispatch_types) ? hubData.dispatch_types : String(hubData.dispatch_types).split(',')).join(', ')}
                </p>
              </div>
            )}

            {/* State */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">{t('forms.state')}</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.state}</p>
            </div>

            {/* Destination Type */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Destination Type</p>
              <p className="font-semibold text-secondary text-base sm:text-lg uppercase">
                {hubData.destination_type_label || destinationTypeLabel(hubData.destination_type)}
              </p>
            </div>

            {/* Parent Hub */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Parent Hub</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {hubData.parent_hub_name
                  ? `${hubData.parent_hub_name}${hubData.parent_hub_code ? ` (${hubData.parent_hub_code})` : ''}`
                  : '—'}
              </p>
            </div>

            {/* Mobile No. */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Mobile No.</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.location_hub_contact || 'N/A'}</p>
            </div>

            {/* Address */}
            <div className="col-span-2 md:col-span-2 bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">{t('common.address')}</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.address}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Latitude</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.latitude ?? '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Longitude</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.longitude ?? '—'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4">Destination Location Hubs</h2>
          <p className="text-xs sm:text-sm text-gray-500 mb-3">
            Source {hubData.location} is locked. Booking and ML creation only offer the hubs below.
          </p>
          {(() => {
            const selected = new Set(
              (Array.isArray(hubData.destination_location_hub_ids)
                ? hubData.destination_location_hub_ids
                : []).map(String),
            );
            const dests = companyHubs.filter((hub) =>
              String(hub.id) !== String(hubData.id) && selected.has(String(hub.id)),
            );
            if (!dests.length) {
              return <p className="text-sm text-gray-500">No destination location hubs selected.</p>;
            }
            return (
              <div className="flex flex-wrap gap-2">
                {dests.map((hub) => (
                  <span
                    key={hub.id}
                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-50 text-blue-800 text-xs sm:text-sm font-medium border border-blue-100"
                  >
                    {formatHubBranchLabel(hub) || hub.location}
                    {hub.location_code ? ` (${hub.location_code})` : ''}
                  </span>
                ))}
              </div>
            );
          })()}
        </div>

        {['allow_paid', 'allow_to_pay', 'allow_on_account', 'allow_delivery', 'allow_booking'].some((key) => hubData[key] !== undefined && hubData[key] !== null) && (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
            <h2 className="text-lg font-bold text-secondary mb-4">Payment & Delivery Capabilities</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              {[
                ['allow_paid', 'Paid'],
                ['allow_to_pay', 'To Pay'],
                ['allow_on_account', 'On Account'],
                ['allow_delivery', 'Delivery'],
                ['allow_booking', 'Booking'],
              ].map(([key, label]) => (
                <div key={key} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">{label}</p>
                  <p className={`font-semibold text-base sm:text-lg ${hubData[key] ? 'text-green-700' : 'text-red-700'}`}>
                    {hubData[key] ? 'Allowed' : 'Not allowed'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4">Additional Information</h2>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Hub ID</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{hubData.hub_id}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">{t('common.createdBy')}</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {hubData.created_by_name || hubData.created_by || 'N/A'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Created Date</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {hubData.created_at ? new Date(hubData.created_at).toLocaleString('en-IN', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/location-hubs/edit/${hubId}`)}>
            Edit Hub
          </Button>
          <Button variant="secondary" onClick={() => navigate('/location-hubs')}>
            Back to List
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewLocationHub;